import HandledErrors, { HandledError } from "@common/consts/errors";
import type { SpaceService } from "@common/services/space-service";
import type UserPreferencesStore from "@common/stores/user-preferences-store";
import type {
  Space,
  SpaceIcon,
  SpaceId} from "@common/types/space";
import {
  CLIENT_DEFAULT_SPACE_ID
} from "@common/types/space";
import { Sort } from "@common/use-cases/sort";
import TodoUtils, { isSameDay } from "@common/utils/todo-utils";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import type SharedTodoStore from "./shared-todo-store";
import type UserStore from "./user-store";
import type { TCommonEvent } from "@common/services/common-events";

export interface SpaceStoreDependencies<
  InjectedTodoStore extends SharedTodoStore = SharedTodoStore
> {
  userStore: UserStore;
  userPreferencesStore: UserPreferencesStore;
  spaceService: SpaceService;
  commonEvents: TCommonEvent;
  makeTodoStore: (
    spaceId: SpaceId,
    spaceStore: SpaceStore
  ) => InjectedTodoStore;
}

export default class SpaceStore<
  InjectedTodoStore extends SharedTodoStore = SharedTodoStore
> {
  @observable
  currentSpaceId: SpaceId = CLIENT_DEFAULT_SPACE_ID;
  @observable
  spaces: Space[] = [];

  @computed
  get spacesSorted() {
    const userId = this.deps.userStore.userId;
    const orderMap =
      this.deps.userPreferencesStore.userPreferences?.spacesSortOrder ?? {};

    return this.spaces.slice().sort((a, b) => {
      // is HOME space
      if (a.id === userId) return -1;
      if (b.id === userId) return 1;
      if (!orderMap[a.id] && orderMap[b.id]) return 1;
      if (!orderMap[b.id] && orderMap[a.id]) return -1;
      // if tie, sort by created date
      if (!orderMap[b.id] && !orderMap[a.id])
        return a.createdDate - b.createdDate;

      const aLexIdx = Sort.decode(orderMap[a.id]!);
      const bLexIdx = Sort.decode(orderMap[b.id]!);

      if (aLexIdx !== bLexIdx) return aLexIdx - bLexIdx;

      return a.name.localeCompare(b.name);
    });
  }

  @observable
  todoStoreMap: Record<string, InjectedTodoStore> = {};
  @observable
  deps: SpaceStoreDependencies<InjectedTodoStore>;

  @computed
  get currentTodoStore() {
    if (!this.todoStoreMap[this.currentSpaceId]) {
      const defaultSpace = this.spaces[0];
      if (!defaultSpace) {
        /**
         * In order to make sure we never reach here, we populate
         * the home store always
         */
        this.deps.commonEvents.emit("LOGOUT");
        console.error(`no store for current space ${this.currentSpaceId}`);
        throw new Error(`no store for current space ${this.currentSpaceId}`);
      }

      runInAction(() => {
        this.currentSpaceId = defaultSpace.id;
      });
      return this.todoStoreMap[defaultSpace.id]!;
    }

    return this.todoStoreMap[this.currentSpaceId]!;
  }

  @computed
  get spaceBadges() {
    return this.spaces.reduce(
      (acc, space) => {
        const todayTodos = Object.values(
          this.todoStoreMap[space.id]?.fullTodoIdMap ?? {}
        ).filter(
          (t) =>
            isSameDay(t.date, new Date()) &&
            t.value.length &&
            !t.completed &&
            !t.deleted
        );
        acc[space.id] = {
          count: todayTodos.length,
          flag: todayTodos.some((t) => TodoUtils.isFlaggable(t)),
        };
        return acc;
      },
      {} as Record<
        string,
        {
          count: number;
          flag: boolean;
        }
      >
    );
  }

  constructor(dependencies: SpaceStoreDependencies<InjectedTodoStore>) {
    this.deps = dependencies;

    runInAction(() => {
      this.todoStoreMap[CLIENT_DEFAULT_SPACE_ID] = this.deps.makeTodoStore(
        CLIENT_DEFAULT_SPACE_ID,
        this
      );
    });

    makeObservable(this);
  }

  /**
   * Like loadSpaces, but for a single space
   *
   * As of this comment, only used for when a user just joins a space,
   * and needs to test if he has access to that space
   *
   * @throws trpc error when space not found
   */
  @action
  async loadSpaceById(spaceId: SpaceId) {
    const existingSpace = this.spaces.find((s) => s.id === spaceId);
    if (existingSpace) return existingSpace; // already loaded
    const space = await this.deps.spaceService.getSpace(spaceId);
    this.todoStoreMap[space.id] = this.deps.makeTodoStore(spaceId, this);
    this.spaces.push(space);
    return space;
  }

  @action
  async loadSpaces(): Promise<void> {
    return new Promise(async (resolve, reject) => {
      const userId = this.deps.userStore.userProfile?.id;
      if (!userId) {
        reject(HandledError.fromHandledErrorJson(HandledErrors.USER_NOT_FOUND));
        return;
      }

      const loadedSpaces =
        (await this.deps.spaceService
          .getSpaces(this.deps.userStore.userId)
          .catch(reject)) ?? [];

      runInAction(() => {
        this.spaces = loadedSpaces;

        // Update spaces in a way that doesn't remove the referenced object to reduce unnecessary re-renders

        // Remove old spaces
        for (const spaceId of Object.keys(this.todoStoreMap)) {
          if (!loadedSpaces.find((s) => s.id === spaceId)) {
            delete this.todoStoreMap[spaceId];
          }
        }

        // Add loaded spaces
        for (const space of loadedSpaces) {
          if (!this.todoStoreMap[space.id]) {
            this.todoStoreMap[space.id] = this.deps.makeTodoStore(
              space.id,
              this
            );
          }
        }

        if (this.currentSpaceId === CLIENT_DEFAULT_SPACE_ID) {
          this.currentSpaceId = userId; // must do initialisation
        }

        if (!this.spaces.find((s) => s.id === this.currentSpaceId)) {
          this.currentSpaceId = userId; // default
        }

        resolve();
      });
    });
  }

  @action
  async createSpace(spaceName: string, icon: SpaceIcon) {
    const newSpace = await this.deps.spaceService.createSpace({
      name: spaceName,
      icon,
    });

    if (!newSpace) return; // BE paywalled

    runInAction(() => {
      this.spaces.push(newSpace);
      this.todoStoreMap[newSpace.id] = this.deps.makeTodoStore(
        newSpace.id,
        this
      );
    });

    // select space
    this.setCurrentSpace(newSpace.id);
  }

  async updateSpace(spaceId: SpaceId, spaceName: string, icon: SpaceIcon) {
    const index = this.spaces.findIndex((s) => s.id === spaceId);
    const oldSpace = this.spaces[index];
    if (!oldSpace) throw new Error(`no space with id ${spaceId}`);

    const updatedSpace = {
      ...oldSpace,
      name: spaceName,
      icon,
    };

    const success = await this.deps.spaceService.updateSpace(
      spaceId,
      updatedSpace
    );
    if (!success) throw new Error(`failed to update space ${spaceId}`);

    return new Promise<void>(async (resolve) => {
      runInAction(() => {
        this.spaces[index] = updatedSpace;
        resolve();
      });
    });
  }

  @action
  async deleteSpace(spaceId: SpaceId) {
    const success = await this.deps.spaceService.deleteSpace(spaceId);
    if (!success) throw new Error(`failed to delete space ${spaceId}`);

    const index = this.spaces.findIndex((s) => s.id === spaceId);
    return new Promise<void>(async (resolve) => {
      runInAction(() => {
        if (spaceId === this.currentSpaceId) {
          const nextSpaceIndex =
            (index - 1 + this.spaces.length) % this.spaces.length;
          const nextSpaceId =
            this.spaces[nextSpaceIndex]?.id ?? this.deps.userStore.userId;
          this.setCurrentSpace(nextSpaceId);
        }

        this.spaces.splice(index, 1);
        delete this.todoStoreMap[spaceId];
        resolve();
      });
    });
  }

  @action
  setCurrentSpace(spaceId: SpaceId) {
    if (this.spaces.find((s) => s.id === spaceId)) {
      this.currentSpaceId = spaceId;
      return;
    }

    this.currentSpaceId = this.deps.userStore.userId; // HOME
  }

  @action
  async migrateTodos(
    todoIds: string[],
    fromSpaceId: SpaceId,
    toSpaceId: SpaceId
  ) {
    const fromTodoStore = this.todoStoreMap[fromSpaceId];
    const toTodoStore = this.todoStoreMap[toSpaceId];

    if (!fromTodoStore || !toTodoStore)
      throw new Error(`no store for space ${fromSpaceId} or ${toSpaceId}`);

    const { updatedTodoIdMap } = TodoUtils.migrateTodos(
      todoIds,
      fromTodoStore.todoIdMap,
      toSpaceId
    );

    const updatedTodos = Object.values(updatedTodoIdMap);
    // update todos
    const success = await this.currentTodoStore.deps.todoService.migrateTodos(
      updatedTodos.map((t) => t.id),
      fromSpaceId,
      toSpaceId
    );

    if (success) {
      // move todos locally
      runInAction(() => {
        for (let updatedTodo of updatedTodos) {
          delete fromTodoStore.fullTodoIdMap[updatedTodo.id];
          toTodoStore.fullTodoIdMap[updatedTodo.id] = updatedTodo;
        }
      });
    } else {
      throw new Error("failed to migrate todos");
    }
  }

  // Space Sharing
  async getSpaceShareLink(spaceId: SpaceId) {
    const shareLinkDetails = await this.deps.spaceService.getShareLinkForSpace(
      spaceId
    );
    return shareLinkDetails;
  }

  async getJoinShareLinkDetails(linkId: string) {
    const shareLinkDetails =
      await this.deps.spaceService.getJoinShareLinkDetails(linkId);
    if (!shareLinkDetails) return null;
    return shareLinkDetails;
  }

  async joinSpaceFromShareLink(linkId: string) {
    const space = await this.deps.spaceService.joinSpaceFromShareLink(linkId);
    this.spaces.push(space);
    this.todoStoreMap[space.id] = this.deps.makeTodoStore(space.id, this);
    this.currentTodoStore.loadTodos();
    this.setCurrentSpace(space.id);
  }

  //========================================================================
  //
  //                     LOCAL CACHE/SESSION PERSISTENCE
  //
  //========================================================================
  async generateCacheState() {
    // NOTE: MUST BE JSON SERIALIZABLE
    const cachedState = {
      version: CACHE_STATE_VERSION, // In case we need to change the format of the state
      spaces: this.spaces,
      currentSpaceId: this.currentSpaceId,
      todoStoreMap: Object.entries(this.todoStoreMap).reduce(
        (acc, [spaceId, todoStore]) => {
          acc[spaceId] = todoStore.generateCacheState();
          return acc;
        },
        {} as Record<SpaceId, ReturnType<SharedTodoStore["generateCacheState"]>>
      ),
    };

    return cachedState;
  }

  /**
   * Used to restore a user session. Must only be used on first load!!! Otherwise
   * the assumptions of which state is incoming will be wrong.
   *
   * Generally best to await before the actual load state, so we don't have to deal with
   * merging of states
   * @param state a json serialisable object to restore state from.
   */
  loadCachedState(
    state: Awaited<ReturnType<SpaceStore["generateCacheState"]>>
  ) {
    if (state.version !== CACHE_STATE_VERSION) return; // don't load, not compatible

    runInAction(() => {
      this.spaces = state.spaces;
      this.todoStoreMap = Object.entries(state.todoStoreMap).reduce(
        (acc, [spaceId, todoStoreCachedState]) => {
          const todoStore = this.deps.makeTodoStore(spaceId, this);
          todoStore.loadCachedState(todoStoreCachedState);
          acc[spaceId] = todoStore;
          return acc;
        },
        {} as Record<SpaceId, InjectedTodoStore>
      );

      if (state.currentSpaceId === CLIENT_DEFAULT_SPACE_ID) {
        console.warn("Default space was stored in cache, skipping");
        const defaultSpace = this.spaces[0];
        if (!defaultSpace) {
          console.error("No default space found, skipping");
          return;
        }
        if (!this.todoStoreMap[defaultSpace.id]) {
          console.error("No todo store for default space, skipping");
          return;
        }
        this.currentSpaceId = defaultSpace.id;
        return;
      }
      // Only set as current space if successfully restored
      if (!this.todoStoreMap[state.currentSpaceId]) return;
      this.currentSpaceId = state.currentSpaceId;
    });
  }
}

export const CACHE_STATE_VERSION = "0.2";
